import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Download, Trash2, Check } from 'lucide-react';
import { formatDuration } from '@/lib/audioUtils';
import { RecordingData } from '@/hooks/useAudioPersistence';

interface RecordingPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  recording: RecordingData | null;
  onDelete: (id: string) => void;
  onGoToAudio: () => void;
}

export function RecordingPreviewDialog({
  isOpen,
  onClose,
  recording,
  onDelete,
  onGoToAudio,
}: RecordingPreviewDialogProps) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);

  useEffect(() => {
    if (recording && isOpen) {
      const url = URL.createObjectURL(recording.blob);
      setAudioUrl(url);
      const audioEl = audioRef.current;
      
      return () => {
        if (audioEl) {
          audioEl.pause();
          audioEl.src = '';
        }
        URL.revokeObjectURL(url);
        setAudioUrl(null);
        setIsPlaying(false);
        setCurrentTime(0);
      };
    }
  }, [recording, isOpen]);

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    onClose();
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleAction = () => {
    handleClose();
    onGoToAudio();
  };

  const handleDelete = () => {
    if (recording) {
      onDelete(recording.id);
      onClose();
    }
  };
  
  const handleSeek = (value: number[]) => {
    const newTime = value[0];
    setCurrentTime(newTime);
  };

  const handleSeekCommit = (value: number[]) => {
    const newTime = value[0];
    setCurrentTime(newTime);
    if (audioRef.current) {
        audioRef.current.currentTime = newTime;
    }
    setIsDragging(false);
  };

  const handleSeekStart = () => {
      setIsDragging(true);
  };

  if (!recording) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      }
    }}>
      <DialogContent className="sm:max-w-md bg-zinc-900 text-white border-zinc-800">
        <DialogHeader>
          <DialogTitle>{t('teleprompter.preview.title', 'Recording Preview')}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {t('teleprompter.preview.description', 'Your recording has been automatically saved. Please review it.')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          {/* Audio Waveform Placeholder */}
          <div className="h-32 bg-zinc-800 rounded-lg flex items-center justify-center relative overflow-hidden group">
               <div className="absolute inset-0 flex items-center justify-center gap-[2px] opacity-50 px-4">
                  {Array.from({ length: 40 }).map((_, i) => (
                      <div 
                          key={i} 
                          className="bg-[#FF6B9D] w-1.5 rounded-full"
                          style={{ height: `${Math.max(20, Math.random() * 80)}%` }} 
                      />
                  ))}
               </div>
               
               <button 
                  onClick={togglePlay}
                  className="z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center text-black hover:scale-105 transition-transform shadow-lg"
               >
                  {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-1" />}
               </button>
          </div>
          
          {/* Progress Bar & Time */}
          <div className="flex flex-col gap-2">
              <Slider 
                  value={[currentTime]} 
                  max={audioDuration || recording.duration} 
                  step={0.1}
                  onValueChange={handleSeek}
                  onValueCommit={handleSeekCommit}
                  onPointerDown={handleSeekStart}
                  className="cursor-pointer"
              />
              
              <div className="flex justify-between text-sm text-zinc-400 font-mono">
                  <span>{formatDuration(currentTime)}</span>
                  <span>{formatDuration(audioDuration || recording.duration)}</span>
              </div>
          </div>

          <audio
            ref={audioRef}
            src={audioUrl || ''}
            onLoadedMetadata={(e) => {
              e.currentTarget.volume = 1;
              const duration = e.currentTarget.duration || 0;
              setAudioDuration(duration);
              if (!isDragging) {
                setCurrentTime(0);
              }
            }}
            onTimeUpdate={(e) => {
                if (!isDragging) {
                    setCurrentTime(e.currentTarget.currentTime)
                }
            }}
            onEnded={() => {
                setIsPlaying(false);
                setCurrentTime(0);
            }}
            className="hidden"
          />
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 w-full">
            <Button
                variant="destructive"
                onClick={handleDelete}
                className="w-full sm:w-auto gap-2 rounded-xl"
            >
                <Trash2 className="w-4 h-4" />
                {t('teleprompter.preview.discard', 'Discard')}
            </Button>
            
            <div className="grid grid-cols-2 gap-3 w-full sm:w-auto">
                <Button 
                    variant="outline" 
                    onClick={handleAction} 
                    className="w-full gap-2 border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 hover:text-white rounded-xl"
                >
                    <Download className="w-4 h-4" />
                    {t('teleprompter.preview.download', 'Download')}
                </Button>
                <Button 
                    onClick={handleAction} 
                    className="w-full gap-2 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-black border-2 border-black font-bold shadow-[4px_4px_0_black] active:translate-y-0.5 active:shadow-none transition-all rounded-xl"
                >
                    <Check className="w-4 h-4" />
                    {t('teleprompter.preview.useAudio', 'Use Audio')}
                </Button>
            </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
